import React, { useEffect, useRef, useState } from 'react';
import Daily from '@daily-co/daily-js';
import { useLocalization } from '../../context/LocalizationContext';
import './MeetingSDK.css';
import Loader from '../shared/Loader';
const MeetingSDK = ({
  roomUrl,
  onError,
  onCallEnd,
  userData
}) => {
  const { t } = useLocalization();
  const callObjectRef = useRef(null);
  const remoteContainerRef = useRef(null);
  const localPiPRef = useRef(null);
  const videoElementsRef = useRef({}); // Track existing video elements
  const [isJoined, setIsJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [participantCount, setParticipantCount] = useState(0);
  const [remoteParticipants, setRemoteParticipants] = useState([]);

  // Timer effect - only start when participants > 1 (doctor has joined)
  useEffect(() => {
    let interval = null;
    if (isJoined && participantCount > 1) {
      interval = setInterval(() => {
        setSessionDuration(prev => prev + 1);
      }, 1000);
    } else if (!isJoined) {
      // Reset timer when call ends
      setSessionDuration(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isJoined, participantCount]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Updated utility function - only creates if doesn't exist, updates tracks if changed
  const getOrCreateVideoElement = (parentElement, videoTrack, audioTrack, sessionId) => {
    let videoEl = videoElementsRef.current[sessionId];

    // Create new element only if it doesn't exist
    if (!videoEl) {
      videoEl = document.createElement('video');
      videoEl.id = `video-${sessionId}`;
      videoEl.autoplay = true;
      videoEl.playsInline = true;

      // The local video needs to be muted
      if (sessionId === 'local') {
        videoEl.muted = true;
      }

      parentElement.appendChild(videoEl);
      videoElementsRef.current[sessionId] = videoEl;
    } else {
      // Move element to correct parent if it's not already there
      if (videoEl.parentElement !== parentElement) {
        parentElement.appendChild(videoEl);
      }
    }

    // Only update srcObject if tracks have changed
    const tracks = [];
    if (videoTrack) tracks.push(videoTrack);
    if (audioTrack && sessionId !== 'local') tracks.push(audioTrack);

    const currentStream = videoEl.srcObject;
    const needsUpdate = !currentStream ||
      currentStream.getTracks().length !== tracks.length ||
      !tracks.every(track => currentStream.getTracks().includes(track));

    if (needsUpdate && tracks.length > 0) {
      videoEl.srcObject = new MediaStream(tracks);
    }

    return videoEl;
  };

  // Function to manage video elements - no longer destroys everything
  const updateParticipantCount = () => {
    if (!callObjectRef.current) return;
    const participants = callObjectRef.current.participants();
    const remote = Object.values(participants).filter(p => !p.local);
    setRemoteParticipants(remote);
    setParticipantCount(Object.keys(participants).length);
  };

  const updateVideos = () => {
    if (!callObjectRef.current || !remoteContainerRef.current || !localPiPRef.current) return;

    const participants = callObjectRef.current.participants();
    const activeSessionIds = new Set();

    // Clear the remote container before re-adding videos
    remoteContainerRef.current.innerHTML = '';

    Object.values(participants).forEach((participant) => {
      const videoTrack = participant.tracks?.video?.track;
      const audioTrack = participant.tracks?.audio?.track;
      const sessionId = participant.local ? 'local' : participant.session_id;

      activeSessionIds.add(sessionId);

      // Render if there's video OR audio
      if (videoTrack || audioTrack) {
        const isLocal = participant.local;
        const container = isLocal ? localPiPRef.current : remoteContainerRef.current;

        // For remote participants, create a wrapper for styling
        if (!isLocal) {
          const wrapper = document.createElement('div');
          wrapper.className = 'remote-video-wrapper';
          container.appendChild(wrapper);
          getOrCreateVideoElement(wrapper, videoTrack, audioTrack, sessionId);
        } else {
          getOrCreateVideoElement(container, videoTrack, audioTrack, sessionId);
        }
      }
    });

    // Remove video elements for participants who left
    Object.keys(videoElementsRef.current).forEach(sessionId => {
      if (!activeSessionIds.has(sessionId)) {
        const videoEl = videoElementsRef.current[sessionId];
        if (videoEl && videoEl.parentElement) {
          videoEl.srcObject = null;
          videoEl.parentElement.removeChild(videoEl);
        }
        delete videoElementsRef.current[sessionId];
      }
    });
  };

  // Setup call and join with better audio settings
  const setupCall = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Create the Daily Call Object with audio quality settings
      callObjectRef.current = Daily.createCallObject({
        audioSource: true,
        videoSource: true,
      });

      // Set up event listeners
      callObjectRef.current
        .on('joined-meeting', () => {
          setIsJoined(true);
          setIsLoading(false);
          updateParticipantCount();
          updateVideos();
          onCallEnd?.(); // Call the callback when meeting is joined
        })

        .on('participant-joined', () => {
          updateParticipantCount();
          updateVideos();
        })
        .on('participant-updated', updateVideos)
        .on('participant-left', () => {
          updateParticipantCount();
          updateVideos();
        })
        .on('track-started', updateVideos) // Add this for better track handling
        .on('left-meeting', () => {
          setIsJoined(false);
        })
        .on('error', (error) => {
          setError(error.message || t('meetingSDK.errorOccurred'));
          onError?.(error.message || t('meetingSDK.errorOccurred'));
        });

      // Join the call with better settings
      await callObjectRef.current.join({
        url: roomUrl,
        audioSource: true,
        videoSource: true,
        userName: userData?.name || "Patient",
      });

      // Set input settings for better audio quality
      await callObjectRef.current.setInputDevicesAsync({
        audioSource: true,
        videoSource: true,
      });

      // Enable audio and video
      await callObjectRef.current.setLocalAudio(true);
      await callObjectRef.current.setLocalVideo(true);

    } catch (err) {
      setError(err.message || t('meetingSDK.failedToJoin'));
      onError?.(err.message || t('meetingSDK.failedToJoin'));
      setIsLoading(false);
    }
  };

  // Cleanup function
  const cleanup = () => {
    // Clean up all video elements
    Object.values(videoElementsRef.current).forEach(videoEl => {
      if (videoEl && videoEl.srcObject) {
        videoEl.srcObject.getTracks().forEach(track => track.stop());
        videoEl.srcObject = null;
      }
      if (videoEl && videoEl.parentElement) {
        videoEl.parentElement.removeChild(videoEl);
      }
    });
    videoElementsRef.current = {};

    if (callObjectRef.current) {
      callObjectRef.current.leave();
      callObjectRef.current.destroy();
      callObjectRef.current = null;
    }
  };

  // Initialize call on mount
  useEffect(() => {
    setupCall();

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [roomUrl]);

  // Handle window beforeunload to ensure proper cleanup
  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanup();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (error) {
    return (
      <div className="meeting-error">
        <h3>{t('meetingSDK.callError')}</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>
          {t('meetingSDK.retry')}
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <Loader text={t('meetingSDK.joiningCall')} />
    );
  }
  if (isJoined && participantCount <= 1) {
    return (
      <Loader text={t('meetingSDK.connectingToDoctor')} />
    );
  }

  return (
    <div className="meeting-sdk">
      {/* Session Timer */}
      {isJoined && (
        <div className="session-timer">
          {formatTime(sessionDuration)}
        </div>
      )}

      {/* Container for remote participants (the background) */}
      <div
        ref={remoteContainerRef}
        className={`remote-videos-container ${remoteParticipants.length > 1 ? 'grid-view' : ''}`}
      />

      {/* Container for the local participant (the small box in front) */}
      <div
        ref={localPiPRef}
        className="local-video-pip"
        id="local-video-pip"
      />
    </div>
  );
};

export default MeetingSDK;